/**
 * Column-specific context menu service. Mirrors `tableContextMenuService.ts`.
 *
 * Two actions:
 * - "Profile column" — opens the Data Profile workspace tab for the column.
 * - "Find usages…" (B7) — opens the Dependency Viewer's definition-text search
 *   seeded with the column name, answering "where is this column used?".
 *
 * ┌─ MERGE NOTE — READ `Docs/FindUsages-MergeContract.md` BEFORE RESOLVING ─┐
 * `feat/new_autocomplete` adds its own `'Find references…'` entry to this same
 * return array, so git WILL conflict here. That is the whole reason this note
 * sits in the conflict hunk: the decision is not one you can make from these
 * few lines.
 *
 * Two things you must know, both verified against running builds on 2026-08-05:
 *
 * 1. **That branch's COLUMN search WAS broken (fixed 2026-09-13).** It returned
 *    zero results from both production entry points (this menu and Monaco
 *    Shift+F12) because it recorded column identifiers unqualified and looked
 *    them up fully qualified — and reported the zero confidently. Since
 *    `fix/find-references-per-database-index`, `identifierIndex.ts`'s
 *    `identifierKey` keys the `'column'` kind BARE on record and lookup alike,
 *    so the qualified target this menu builds finds the bare records; the
 *    host relation stays the cross-table disambiguator (`hostTable` vs
 *    `containingTable`). Screenshots of the old behaviour:
 *    `Ralph/FindUsagesMergeCompare/`.
 * 2. **The two searches are not interchangeable and must never be silently
 *    merged into one result list.** A text hit is a lead (it also matches
 *    comments and dynamic SQL); a catalog or index hit is a fact. The whole
 *    value of each depends on the user seeing which one they are looking at.
 *
 * An earlier version of this note said "keep BOTH entries with their existing
 * labels". That is superseded — the contract's screenshot-verified
 * recommendation is ONE entry converging on the References surface, gated on
 * the column bug above being fixed and this side's honesty labelling being
 * carried over. Follow the contract, not this comment, if they ever disagree
 * again.
 * └──────────────────────────────────────────────────────────────────────────┘
 */

import type { DatabaseEngineId } from '@/core/engines';

import type { ContextMenuAction, TreeNode } from '../../explorer/models/TreeModel';
import type { ColumnTreeNode } from '../../explorer/models/nodeTypes';
import { isColumnNode } from '../../explorer/models/nodeTypeGuards';
import { toast } from '../../../components/ui/toaster';
import { openDataProfileTab } from '../../workspace/data-profile/services/dataProfileNavigation';
import { openFindUsagesTab } from '../../workspace/dependency-viewer/services/findUsagesNavigation';
import { FIND_USAGES_MENU_LABEL } from '../../workspace/dependency-viewer/services/findUsagesMenu';
import { isFindUsagesSupportedEngine } from '../../workspace/dependency-viewer/services/findUsagesEngine';

type ColumnContextMenuActionType = 'profile-column' | 'find-usages';

const ColumnActions = {
  PROFILE_COLUMN: 'profile-column' as ColumnContextMenuActionType,
  FIND_USAGES: 'find-usages' as ColumnContextMenuActionType,
};

export function createProfileColumnAction(): ContextMenuAction {
  return {
    id: ColumnActions.PROFILE_COLUMN,
    label: 'Profile column',
    icon: 'bar-chart',
  };
}

export function createFindUsagesAction(): ContextMenuAction {
  return {
    id: ColumnActions.FIND_USAGES,
    label: FIND_USAGES_MENU_LABEL,
    icon: 'file-search',
  };
}

export function generateColumnContextMenuActions(
  engine?: DatabaseEngineId | string,
): ContextMenuAction[] {
  const actions = [createProfileColumnAction()];
  // Kusto/ADX speaks KQL and has no SQL module catalog — offering the entry
  // point there would hand the user a T-SQL query and a parser error.
  if (isFindUsagesSupportedEngine(engine)) actions.push(createFindUsagesAction());
  return actions;
}

export function executeColumnContextMenuAction(
  actionId: string,
  _nodeId: string,
  node?: TreeNode,
): void {
  if (actionId !== ColumnActions.PROFILE_COLUMN && actionId !== ColumnActions.FIND_USAGES) return;
  if (!node || !isColumnNode(node)) {
    toast.error({ title: 'Error', description: 'Column node not found' });
    return;
  }
  const columnNode = node as ColumnTreeNode;
  const meta = columnNode.metadata;

  if (actionId === ColumnActions.FIND_USAGES) {
    openFindUsagesTab({
      connectionId: meta.connectionId,
      database: meta.database,
      schema: meta.schema,
      table: meta.tableName,
      columnName: meta.columnName,
      source: 'object_explorer_column',
    });
    return;
  }

  openDataProfileTab({
    connectionId: meta.connectionId,
    database: meta.database,
    schema: meta.schema,
    table: meta.tableName,
    focusedColumn: meta.columnName,
    entryPoint: 'object_explorer_column',
  });
}
